import React, { useContext, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "./FanTabBar.css";
import { useKeyboardOpen } from "../hooks/useKeyboardOpen";
import { trackTabBar } from "../lib/tabBarVars";
import { t as tr } from "../i18n";

// App fan mode — the 3-tab emoji bar (locked r6 §5): 🔍 Browse /
// 🎟️ My Requests / 🏆 Leaderboard. Founder locked the SIMPLE bar — emoji +
// tiny label; the glass pill + SVG icons the DJ bar uses were rejected for
// fans. Profile is the header chip, NOT a tab.
//
// Shows only on the three fan tab routes, and never for a signed-in talent
// (1.5.3: a talent who paid on another artist's page keeps their own bar
// there — BottomTabBar maps these routes for them), so it can never coexist
// with the DJ bar. Like the DJ bar it tells the pages where it is
// (lib/tabBarVars) so <main> reserves the space.

const TABS = [
  { to: "/fan", emoji: "🔍", label: tr("fanTabBar.browse") },
  { to: "/fan/requests", emoji: "🎟️", label: tr("fanTabBar.myRequests") },
  { to: "/fan/leaderboard", emoji: "🏆", label: tr("fanTabBar.leaderboard") },
] as const;

const FanTabBar: React.FC = () => {
  const { pathname } = useLocation();
  const barRef = useRef<HTMLElement>(null);
  const keyboardOpen = useKeyboardOpen(); // see BottomTabBar
  const { user, isAuthenticated } = useContext(AuthContext);
  const talentSignedIn = isAuthenticated && !!user;
  const visible = TABS.some((t) => t.to === pathname) && !keyboardOpen && !talentSignedIn;

  useEffect(() => {
    if (!visible) return;
    const el = barRef.current;
    if (!el) return;
    return trackTabBar(el);
  }, [visible]);

  if (!visible) return null;

  return (
    <nav className="fantabbar" ref={barRef} aria-label={tr("fanTabBar.fan")}>
      {TABS.map((t) => {
        const active = pathname === t.to;
        return (
          <Link
            key={t.to}
            to={t.to}
            className={`fantab${active ? " fantab--on" : ""}`}
            aria-current={active ? "page" : undefined}
          >
            <span className="fantab-ico" aria-hidden>
              {t.emoji}
            </span>
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
};

export default FanTabBar;
